import React, { useState } from 'react'
import CancelIcon from '@mui/icons-material/Cancel'
import { Typography } from '@mui/material'

import { FilteringOption } from 'constants/Inspection/filterByCriteria'
import { textFiles } from 'constants/textFiles'
import useTranslation from 'hooks/useTranslation'
import { CurboSpot } from 'models/curboSpot'
import { ListingFilterType } from 'models/services/base'
import { Provider } from 'models/services/provider'
import { ExtendedStatus } from 'models/services/status'

import { StyledCard } from './style'

export type FilterInputType = 'number' | 'string' | 'boolean'

export type Filter = {
  fieldOrEntry: string
  operator: string
  valueToCompare: string | number
}

type FilterCardType = {
  filter?: Filter | null
  filterIdx?: number
  viewOnly?: boolean
  field?: string | null
  /**
   * Optional string for different text file
   */
  file?: string
  /**
   * List of the new filters, stored in state but NOT STILL applied
   */
  newFiltersList?: Filter[]
  /**
   * Function to update the value of the filters list that are NOT STILL applied,
   * so you can remove filters from it before pushing the filters you want applied.
   */
  handleNewFiltersList?: (
    newFiltersList: Filter[],
    filterInput: ListingFilterType
  ) => void

  filterKey?: string

  filterInput?: ListingFilterType

  statusList?: ExtendedStatus[]

  curboSpots?: CurboSpot[]

  providerList?: Provider[]

  options?: Record<string, FilteringOption[]>
}

const FilterCard = ({
  filter,
  filterIdx,
  viewOnly = false,
  field,
  file = textFiles.INSPECTION_LISTING,
  newFiltersList,
  handleNewFiltersList,
  filterKey,
  filterInput,
  statusList,
  curboSpots,
  providerList,
  options,
}: FilterCardType) => {
  const [isActive, setIsActive] = useState(true)

  const { text } = useTranslation(file)

  const { fieldOrEntryOptions, operatorOptions, provinceOptions } =
    text.filtersByCriteria

  const removeFilter = () => {
    setIsActive(!isActive)

    /**
     * Remove the filter from the "newFiltersList" array. It contains the temporary filters
     * to apply.
     */
    if (newFiltersList && handleNewFiltersList && filterInput) {
      const testArray: Filter[] = [...newFiltersList]
      if (filterIdx !== undefined) testArray.splice(filterIdx, 1)

      let newFilterInput = { ...filterInput }
      if (filterKey) {
        const filterArray = filterKey.split(':')
        const key = filterArray[0]
        const value = filterArray[1]
        const newArrayValue = newFilterInput[key].filter((input) => {
          return typeof input === 'string'
            ? input !== value
            : input !== Number(value)
        })
        newFilterInput = { ...newFilterInput, [key]: newArrayValue }
      }
      handleNewFiltersList(testArray, newFilterInput)
    }
  }

  const getResultLabel = () => {
    if (filter) {
      const entry = filter?.fieldOrEntry
      const value = filter?.valueToCompare
      if (entry === 'province') {
        return provinceOptions[value]
      }
      if (entry === 'status') {
        const displayStatus = statusList?.find(
          (status) => status.slug === value
        )

        return displayStatus ? displayStatus.name : value
      }
      if (entry === 'curboSpot' && curboSpots) {
        const displayCurboSpot = curboSpots?.find(
          (curboSpot) => curboSpot.id === value
        )
        return displayCurboSpot ? displayCurboSpot.name : value
      }
      if (entry === 'provider' && providerList) {
        const displayProvider = providerList?.find(
          (provider) => provider.id === value
        )

        return displayProvider ? displayProvider.name : value
      }

      if (options && entry in options) {
        const displayOption = options[entry].find(
          (option) => option.value === value
        )
        return displayOption ? displayOption.name : value
      }
    }

    return filter?.valueToCompare
  }

  return isActive ? (
    <StyledCard>
      {filter ? (
        <Typography variant="subtitle1">{`"${
          fieldOrEntryOptions[filter.fieldOrEntry]
        }" ${
          operatorOptions[filter.operator]
        } "${getResultLabel()}"`}</Typography>
      ) : null}
      {field ? <Typography variant="subtitle1">{field}</Typography> : null}
      {viewOnly ? null : <CancelIcon onClick={removeFilter} />}
    </StyledCard>
  ) : null
}

export default FilterCard
